"use client";

import { useState, useRef, useEffect } from "react";
import React from "react";
import { createPortal } from "react-dom";
import HeaderNavModal from "@components/atoms/HeaderNavModal";
import { ApiFilterResponse } from '@interfaces/filter.types';

interface HeaderNavModalClientProps {
  children: React.ReactNode;
  type: 'blog' | 'buyCar';
  headerNavData: any;
  filterData?: ApiFilterResponse | null;
}

const HeaderNavModalClient: React.FC<HeaderNavModalClientProps> = ({
  children,
  type,
  headerNavData,
  filterData
}) => {
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    position: number;
    top: number;
  }>({
    isOpen: false,
    position: 0,
    top: 0
  });

  const timeoutRef = useRef<number | null>(null);
  const isMouseOverHeaderNavModalRef = useRef(false);
  const isMouseOverNewCarNavModalRef = useRef(false);
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return () => {
      clearTimeoutRef();
    };
  }, []);

  const clearTimeoutRef = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const closeModal = () => {
    setModalState({
      isOpen: false,
      position: 0,
      top: 0
    });
    clearTimeoutRef();
  };

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    clearTimeoutRef();
    if (buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      // Position modal above the button, accounting for header height
      const headerHeight = 70; // Approximate header height
      const calculatedTop = Math.max(headerHeight, buttonRect.top);
      
      setModalState({
        isOpen: true,
        position: buttonRect.left,
        top: calculatedTop
      });
    }
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      if (!isMouseOverHeaderNavModalRef.current && !isMouseOverNewCarNavModalRef.current) {
        closeModal();
      }
    }, 300) as unknown as number;
  };

  const onHeaderNavModalHover = (isHovering: boolean) => {
    isMouseOverHeaderNavModalRef.current = isHovering;
    if (isHovering) {
      clearTimeoutRef();
    } else {
      handleMouseLeave();
    }
  };

  const onNewCarNavModalHover = (isHovering: boolean) => {
    isMouseOverNewCarNavModalRef.current = isHovering;
    if (isHovering) {
      clearTimeoutRef();
    } else {
      handleMouseLeave();
    }
  };

  return (
    <>
      <div
        ref={buttonRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>

      {modalState.isOpen && typeof window !== 'undefined' && createPortal(
        <HeaderNavModal
          headerNavData={headerNavData}
          onClose={closeModal}
          type={type}
          position={modalState.position}
          top={modalState.top}
          onMouseEnter={() => onHeaderNavModalHover(true)}
          onMouseLeave={() => onHeaderNavModalHover(false)}
          onNewCarNavModalHover={onNewCarNavModalHover}
          filterData={filterData || null}
        />,
        document.body
      )}
    </>
  );
};

export default HeaderNavModalClient;
